import { X } from "lucide-react";
import { useCallback, useState } from "react";
import type { FilterPartComponentProps, FilterStep } from "@/types/filter";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import FilterButton from "./FilterButton";
import { filterOperators, useTaskFilterValues } from "./filters";

export default function FilterPartComponent({
    filter,
    isActive,
    onActivate,
    onUpdateType,
    onUpdateOperator,
    onUpdateValue,
    onRemove,
}: FilterPartComponentProps) {
    const taskFilterValues = useTaskFilterValues();
    const [step, setStep] = useState<FilterStep | null>(!filter.type ? "type" : !filter.operator ? "operator" : "value");

    const filterValue = taskFilterValues.find((fv) => fv.type === filter.type);
    const typeLabel = filterValue?.label || "Type";
    const operator = filterValue?.operators.find((op) => op === filter.operator);
    const operatorLabel = operator ? filterOperators[operator as keyof typeof filterOperators] : "is";

    const valueLabel = filterValue?.valueLabel(filter.value) || "-";

    const isStepTypeOpen = isActive && step === "type";
    const isStepOperatorOpen = isActive && step === "operator";
    const isStepValueOpen = isActive && step === "value";

    const typeHasBeenSelected = !!filter.type;
    const operatorHasBeenSelected = !!filter.operator;
    const valueHasBeenSelected = !!filter.value;

    const isCompleted = typeHasBeenSelected && operatorHasBeenSelected && valueHasBeenSelected;

    const handleTypeSelect = (type: string) => {
        onUpdateType(type);
        if (!isCompleted) {
            setStep("operator");
        }
    };

    const handleOperatorSelect = (operator: string) => {
        onUpdateOperator(operator);
        if (!isCompleted) {
            setStep("value");
        } else {
            setStep(null);
        }
    };

    const handleValueSelect = useCallback(
        (value: any, closePopup: boolean = true) => {
            onUpdateValue(value);
            if (closePopup) {
                setStep(null);
            }
        },
        [onUpdateValue]
    );

    const handleOpenChangeStepType = (open: boolean) => {
        if (!open) {
            setStep(null);
        } else {
            onActivate();
        }
    };

    const handleOpenChangeStepOperator = (open: boolean) => {
        if (!open) {
            setStep(null);
        } else {
            onActivate();
        }
    };

    const handleOpenChangeStepValue = (open: boolean) => {
        if (!open) {
            setStep(null);
        } else {
            onActivate();
        }
    };

    // Get the display values for each part

    return (
        <div className="flex items-center">
            <div className="flex rounded-sm overflow-hidden border shadow-xs">
                {/* Type Button */}
                <Popover open={isStepTypeOpen} onOpenChange={handleOpenChangeStepType}>
                    <PopoverTrigger
                        render={
                            <FilterButton
                                position="left"
                                className={typeHasBeenSelected ? "pointer-events-none" : ""}
                                onClick={() => setStep("type")}
                            >
                                {typeLabel}
                            </FilterButton>
                        }
                    ></PopoverTrigger>
                    <PopoverContent className="w-60 p-0" align="start">
                        <Command>
                            <CommandInput placeholder="Search filter type..." />
                            <CommandList>
                                <CommandEmpty>No filter type found.</CommandEmpty>
                                <CommandGroup>
                                    {taskFilterValues.map((fv) => (
                                        <CommandItem key={fv.type} onSelect={() => handleTypeSelect(fv.type)}>
                                            {fv.icon && <fv.icon className="size-4" />}
                                            {fv.label}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                {/* Operator Button */}
                <Popover open={isStepOperatorOpen} onOpenChange={handleOpenChangeStepOperator}>
                    <PopoverTrigger
                        render={
                            <FilterButton position="middle" disabled={!typeHasBeenSelected} onClick={() => setStep("operator")}>
                                {operatorLabel}
                            </FilterButton>
                        }
                    ></PopoverTrigger>
                    {!!filterValue && (
                        <PopoverContent className="w-60 p-0" align="start">
                            <Command>
                                <CommandList>
                                    <CommandGroup>
                                        {filterValue.operators.map((op) => (
                                            <CommandItem key={op} onSelect={() => handleOperatorSelect(op)}>
                                                {filterOperators[op as keyof typeof filterOperators]}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    )}
                </Popover>

                {/* Value Button */}
                <Popover open={isStepValueOpen} onOpenChange={handleOpenChangeStepValue}>
                    <PopoverTrigger
                        render={
                            <FilterButton
                                position="right"
                                disabled={!typeHasBeenSelected || !operatorHasBeenSelected}
                                onClick={() => setStep("value")}
                            >
                                {valueLabel}
                            </FilterButton>
                        }
                    ></PopoverTrigger>
                    {!!filterValue && (
                        <PopoverContent className="w-full max-w-80 p-0" align="start">
                            <filterValue.selectComponent onChange={handleValueSelect} value={filter.value} />
                        </PopoverContent>
                    )}
                </Popover>

                {/* Remove Button - Now part of the button group */}
                <FilterButton className="px-1.5!" position="right" onClick={onRemove}>
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove</span>
                </FilterButton>
            </div>
        </div>
    );
}
